import {
  Configuration,
  DefaultApiGetCachedinterdaychartsRequest,
  DefaultApiGetCachedintradaychartsRequest,
  DefaultApiSearchInstrumentListRequest,
  DefaultApi as EstreamApi,
  InstrumentListItem,
} from '@eih-netbroker/api-estream';
import { RawAxiosRequestConfig } from 'axios';
import { isAfter, isWithinInterval, startOfDay } from 'date-fns';
import { PrimitiveAtom } from 'jotai';
import { IConfig, Keys } from '../../config';
import { eStreamSessionIdAtom, Store } from '../../state';
import {
  Currency,
  CurrencyCross,
  EStreamAllDataDTO,
  EStreamCachedChartDTO,
  EStreamChartDTO,
  EStreamChartEntry,
  EStreamTradeDTO,
  EStreamTradeHistoryDTO,
  GetBETTradesResponse,
  getInstrumentIdForce,
  InstrumentSubscriptionDataScope,
  InstrumentSubscriptionMap,
  InstrumentSubscriptionRequestItem,
  ListedInstrument,
  ListedInstrumentLike,
  QuoteFeedInterval,
  SubscribeOptions,
} from '../../types';
import {
  formatDateTimeToEStream,
  isArrayEqual,
  isAxiosError,
  NbLogger,
  ObjectSet,
  sumOfFieldInList,
  uniqueArray,
} from '../../utils';
import { ResourceHeadersWithToken } from '../useResource';
import { CurrencyExchangeRateRT } from './currencyExchangeRateRT';
import { getRicForChartData, getRicForInstrumentData, replaceSourceDataScope } from './eStreamUtils';
import { EventSourceLike, SSEClient } from './sseClient';

export type GetSearchInstrumentListParams = Omit<
  DefaultApiSearchInstrumentListRequest,
  keyof (ResourceHeadersWithToken & { estreamSessionId: string })
>;

const getInstrumentSubscriptionId = (item: ListedInstrument | ListedInstrumentLike): string =>
  `${item.ric}_${item.mic}`;

enum TokenInitializingState {
  NotInitialized = 'NotInitialized',
  Initializing = 'Initializing',
  Initialized = 'Initialized',
  Disconnected = 'Disconnected',
}

enum EStreamChartType {
  /** Used for intraday charts, when the from and to are in the same day and minute  */
  IntraDay = 'intraday',
  InterDay = 'interday',
}

type EStreamChartRequestParameters = {
  type: EStreamChartType;
  ric: string;
  from: Date;
  to: Date;
};

export class EStreamClient extends SSEClient<Array<EStreamTradeDTO> | EStreamTradeDTO> {
  private instrumentSubscription: ObjectSet<InstrumentSubscriptionMap> = new ObjectSet<
    InstrumentSubscriptionMap,
    string
  >(getInstrumentSubscriptionId);
  private _subscriptions?: Array<InstrumentSubscriptionRequestItem>;
  private tokenInitializingState = TokenInitializingState.NotInitialized;
  private sendSubscribeLater?: number;
  protected override validMessageType = 'prices';
  private eStreamApi?: EstreamApi;
  private eStreamSessionIdRefresh?: number;
  private refreshMarketRightsOnNextSubscribe?: boolean;
  private firstSubscriptionSent = false;
  private tokenExpiration?: number;

  constructor(
    readonly clientConstructor: (token: string) => EventSourceLike,
    protected readonly store: Store,
    protected logger: NbLogger,
    protected sseEventLogger: ((message: any) => void) | null,
    protected config: IConfig,
    protected currencyExchangeRateRT: CurrencyExchangeRateRT,
  ) {
    super(clientConstructor, store, logger, sseEventLogger);
  }

  override async connect(isSilent?: boolean): Promise<void> {
    if (!this._subscriptions?.length) {
      this.logger.warn(`No subscriptions to eStream, skipping connection`);
      return;
    }
    if (this.tokenInitializingState === TokenInitializingState.Initializing) {
      this.logger.warn(`No subscriptions to eStream, skipping connection`);
      return;
    }

    if (this.isTokenExpired()) {
      this.logger.warn(`eStream token expired, reinitializing session`);
      this.invalidateSession();
    }

    // TODO eStream should initialize token on first connection, but it doesn't, it need a subscription request first
    if (
      this.tokenInitializingState === TokenInitializingState.NotInitialized ||
      this.tokenInitializingState === TokenInitializingState.Disconnected
    ) {
      this.tokenInitializingState = TokenInitializingState.Initializing;

      // make sure to send the subscription request first if it's not sent yet
      if (!this.firstSubscriptionSent) {
        const subscriptionRequestOk = await this.sendSubscriptionRequest();
        if (!subscriptionRequestOk) {
          this.tokenInitializingState = TokenInitializingState.Disconnected;
          this.eventHandlers?.onConnectError(!!isSilent);
          return;
        }
      }
    }

    if (!this.token) {
      this.logger.warn(`eStream token is missing, skipping connection`);
      this.tokenInitializingState = TokenInitializingState.Disconnected;
      this.eventHandlers?.onConnectError(!!isSilent);
      return;
    }

    return super.connect(isSilent);
  }

  override onConnectSuccess() {
    this.tokenInitializingState = TokenInitializingState.Initialized;
  }

  get subscriptions(): Array<InstrumentSubscriptionRequestItem> {
    return this._subscriptions || [];
  }

  get eStreamSessionId() {
    return this.store.get(eStreamSessionIdAtom);
  }

  set eStreamSessionId(val: string | undefined) {
    this.store.set(eStreamSessionIdAtom as PrimitiveAtom<string | undefined>, val);
  }

  private get eStreamBaseURL(): string {
    return `${this.config.API.eStreamBaseUrl}${this.config.API.usUrl.eStream}`;
  }

  /**
   * Sends a subscribe request to eStream
   * @param subscriptionChanges if not defined, the previous subscription list will be used. Useful for reconnect purposes
   */
  handleSubscriptions(subscriptionChanges?: ObjectSet<SubscribeOptions>): void {
    if (subscriptionChanges) {
      // if there is no change in the subscriptions we don't need to update anything
      if (subscriptionChanges.keys().length === 0) {
        return;
      }
      this.updateSubscriptionMap(subscriptionChanges);
    }

    const newSubscriptions: Array<InstrumentSubscriptionRequestItem> = this.instrumentSubscription
      .values()
      .map(subscription => ({
        mic: subscription.mic,
        ric: subscription.ric,
        rt: true,
        datascopes: uniqueArray(Object.values(subscription.source).flat()),
      }))
      .filter(subscription => subscription.datascopes.length > 0);

    // if subscriptions has not changed, no need to send another request
    if (!subscriptionChanges || (this._subscriptions && isArrayEqual(newSubscriptions, this._subscriptions))) {
      return;
    }

    this._subscriptions = newSubscriptions;

    this.sendSubscriptionRequestLater();
  }

  async findInstruments(
    instruments: Array<ListedInstrument | ListedInstrumentLike>,
    dataScopes: Array<InstrumentSubscriptionDataScope>,
  ): Promise<Array<EStreamAllDataDTO>> {
    const res = await this.executeEStreamRequest((eStreamApi, headers) =>
      eStreamApi.getInstruments(
        {
          ...headers,
          filterRic: instruments.map(instrument => getInstrumentIdForce(instrument)).map(getRicForInstrumentData),
          filterDatascopes: [dataScopes.join(',')],
        },
        { baseURL: this.eStreamBaseURL },
      ),
    );
    return res.data.data as Array<EStreamAllDataDTO>;
  }

  override disconnect() {
    this.firstSubscriptionSent = false;
    if (this.sendSubscribeLater) {
      clearTimeout(this.sendSubscribeLater);
    }
    if (this.eStreamSessionIdRefresh) {
      clearTimeout(this.eStreamSessionIdRefresh);
    }

    super.disconnect();
    if (this.eStreamApi && this.eStreamSessionId && this.getResourceHeadersWithToken) {
      this.eStreamApi
        .deleteSessions(
          { ...this.getResourceHeadersWithToken(), estreamSessionId: this.eStreamSessionId },
          {
            baseURL: this.eStreamBaseURL,
          },
        )
        .catch(err => {
          console.log(`Failed to delete eStream session`, err);
        });
    }

    this.invalidateSession();
  }

  async getChartData(
    pric: string,
    interval: QuoteFeedInterval,
    from: Date,
    to: Date,
    abortController?: AbortController,
  ): Promise<EStreamChartDTO> {
    const today = new Date();
    const ric = getRicForChartData(pric);
    if (interval === 'minute' || interval === 'second') {
      const res = await this.getIntraDayChart({ ric, from, to }, abortController);
      return res.data as EStreamChartDTO;
    } else if (
      isWithinInterval(startOfDay(today), {
        end: to,
        start: from,
      })
    ) {
      const interDayResPr = this.getInterDayChart({ ric, from, to });
      const intraDayResPr = this.getIntraDayChart({ ric, from: startOfDay(today), to: today });
      const responses = await Promise.all([interDayResPr, intraDayResPr]);
      const interDayRes = responses[0];
      const intraDayRes = responses[1];

      const intraDayData = intraDayRes.data.data;
      if (intraDayData.length > 0) {
        const lastCandle: EStreamChartEntry = {
          H: Math.max(...intraDayData.map(_ => _.H)),
          L: Math.min(...intraDayData.map(_ => _.L)),
          V: sumOfFieldInList(intraDayData, 'V'),
          C: intraDayData[intraDayData.length - 1].C,
          O: intraDayData[0].O,
          T: intraDayData[0].T,
        };
        return {
          data: [...interDayRes.data.data, lastCandle],
          meta: interDayRes.data.meta,
        } as EStreamChartDTO;
      }

      return {
        data: interDayRes.data.data,
        meta: interDayRes.data.meta,
      };
    } else {
      const res = await this.getInterDayChart({ ric, from, to });
      return res.data as EStreamChartDTO;
    }
  }

  async getBETTrades(fromDate: Date, limit?: number, fromMetaLastId?: string): Promise<GetBETTradesResponse> {
    const res = await this.executeEStreamRequest((eStreamApi, headers) =>
      eStreamApi.getTrades(
        {
          ...headers,
          filterMic: Keys.Markets.dataProviderMicList.bet.join(','),
          filterDate: formatDateTimeToEStream(fromDate).split('T')[0],
          limit,
          from: fromMetaLastId,
        },
        { baseURL: this.eStreamBaseURL },
      ),
    );

    return { data: (res.data.data || []) as unknown as Array<EStreamTradeHistoryDTO>, meta: res.data.meta };
  }

  async search(params: GetSearchInstrumentListParams): Promise<Array<InstrumentListItem>> {
    const res = await this.executeEStreamRequest((eStreamApi, headers) =>
      eStreamApi.searchInstrumentList(
        {
          ...headers,
          ...params,
        },
        { baseURL: this.eStreamBaseURL },
      ),
    );
    return res.data.data ?? [];
  }

  async getEStreamSessionId(): Promise<string | null> {
    if (!this.token) {
      return null;
    }
    if (!this.eStreamSessionId) {
      try {
        await this.getEstreamApi();
      } catch (err) {
        console.error('Could not get eStream session ID ', err);
        return null;
      }
    }
    return this.eStreamSessionId ?? null;
  }

  async getExchangeRate(
    currencyCrosses: Array<{ base: Currency; quote: Currency }>,
  ): Promise<Map<CurrencyCross, number>> {
    return this.currencyExchangeRateRT.getExchangeRate({
      currencyCrosses,
      findInstruments: this.findInstruments.bind(this),
    });
  }

  /**
   * Throttles EStream subscription requests
   * @private
   */
  private sendSubscriptionRequestLater() {
    if (this.sendSubscribeLater) {
      clearTimeout(this.sendSubscribeLater);
    }

    this.sendSubscribeLater = setTimeout(() => {
      this.sendSubscriptionRequest();
    }, this.config.SSE.eStreamSubscribeThrottleTimeout) as unknown as number;
  }

  /**
   * Sends a subscribe request to eStream
   * @private
   */
  private async sendSubscriptionRequest(): Promise<boolean> {
    if (!this._subscriptions || this._subscriptions.length === 0) {
      this.logger.error(`Subscription list is not defined`);
      return false;
    }

    this.firstSubscriptionSent = true;
    try {
      const { eStreamApi, headers } = await this.prepareEStreamRequest();
      await eStreamApi.putSubcriptions(
        {
          ...headers,
          instrumentSubscriptionSchema: this._subscriptions.map(subscription => ({
            ric: subscription.ric,
            mic: subscription.mic,
            datascopes: subscription.datascopes,
            rt: 'true',
          })),
          refreshMarketRights: this.refreshMarketRightsOnNextSubscribe || undefined,
        },
        { baseURL: this.eStreamBaseURL },
      );
    } catch (err) {
      this.firstSubscriptionSent = false;
      this.invalidateSession();
      console.error('Could not send subscriptionRequest ', err);
      return false;
    }

    if (this.refreshMarketRightsOnNextSubscribe) {
      this.refreshMarketRightsOnNextSubscribe = false;
    }

    if (
      this.tokenInitializingState === TokenInitializingState.NotInitialized ||
      this.tokenInitializingState === TokenInitializingState.Disconnected
    ) {
      this.connect();
    }
    return true;
  }

  /**
   * Updates and merges the subscription map with the new subscription list
   * @param subscriptionUpdates an ObjectSet describing the subscription changes
   * @private
   */
  private updateSubscriptionMap(subscriptionUpdates: ObjectSet<SubscribeOptions>) {
    this.instrumentSubscription.clear();

    // add subscriptions
    subscriptionUpdates.forEach(subscription => {
      const subscriptionId = getInstrumentSubscriptionId(subscription.instrument);

      const newSubscription: InstrumentSubscriptionMap = {
        ric: subscription.instrument.ric,
        mic: subscription.instrument.mic,
        source: {
          [subscription.source]: [...subscription.dataScope],
        },
      };
      const subscriptionSrc = subscription.source;

      // when there is a subscription for the instrument we try to create/update the old subscriptions
      // by modifying data scopes or setting up data scope
      // if (subscription.type === 'subscribe') {
      this.instrumentSubscription.set(subscriptionId, newSubscription, updatedItem => {
        // if the source is already added we need to extend data scope
        if (updatedItem.source[subscriptionSrc]) {
          return replaceSourceDataScope(updatedItem, subscriptionSrc, existingDataScope =>
            uniqueArray([...existingDataScope, ...subscription.dataScope]),
          );
        } else {
          // if there is no source yet, create a datascope for that source
          return replaceSourceDataScope(updatedItem, subscriptionSrc, () => [...subscription.dataScope]);
        }
      });
    });
  }

  private getIntraDayChart(params: Omit<EStreamChartRequestParameters, 'type'>, abortController?: AbortController) {
    return this.getChart({ type: EStreamChartType.IntraDay, ...params }, abortController);
  }

  private getInterDayChart(params: Omit<EStreamChartRequestParameters, 'type'>, abortController?: AbortController) {
    return this.getChart({ type: EStreamChartType.InterDay, ...params }, abortController);
  }

  private async getChart({ type, ric, from, to }: EStreamChartRequestParameters, abortController?: AbortController) {
    return this.executeEStreamRequest((eStreamApi, headers) => {
      if (type === 'intraday') {
        return eStreamApi.getIntradaycharts(
          {
            ...headers,
            filterRic: ric,
            filterStarttime: formatDateTimeToEStream(from),
            filterEndtime: formatDateTimeToEStream(to),
          },
          {
            baseURL: this.eStreamBaseURL,
            signal: abortController?.signal,
          },
        );
      }
      return eStreamApi.getInterdaycharts(
        {
          ...headers,
          filterRic: ric,
          filterStarttime: formatDateTimeToEStream(from),
          filterEndtime: formatDateTimeToEStream(to),
        },
        { baseURL: this.eStreamBaseURL },
      );
    });
  }

  /**
   * @returns All available entries for currently open session (1 minute tick)
   */
  public getCachedIntraDayChart(ric: string, abortController?: AbortController) {
    return this.getCachedChart(EStreamChartType.IntraDay, ric, abortController);
  }

  /**
   * @returns All available entries max 20 years back (1 day tick)
   */
  public getCachedInterDayChart(ric: string, abortController?: AbortController) {
    return this.getCachedChart(EStreamChartType.InterDay, ric, abortController);
  }

  private async getCachedChart(
    type: EStreamChartType,
    ric: string,
    abortController?: AbortController,
  ): Promise<EStreamCachedChartDTO> {
    const rawConfig: RawAxiosRequestConfig = {
      baseURL: this.eStreamBaseURL,
      signal: abortController?.signal,
    };
    const response = await this.executeEStreamRequest((eStreamApi, headers) => {
      // Both request types should be the same.
      const request: DefaultApiGetCachedinterdaychartsRequest & DefaultApiGetCachedintradaychartsRequest = {
        ...headers,
        ric,
      };
      return type === 'intraday'
        ? eStreamApi.getCachedintradaycharts(request, rawConfig)
        : eStreamApi.getCachedinterdaycharts(request, rawConfig);
    });

    return response.data;
  }

  private getEStreamRequestHeaders(): ResourceHeadersWithToken & { estreamSessionId: string } {
    if (!this.getResourceHeadersWithToken) {
      throw new Error('getResourceHeaders is not defined');
    }

    const estreamSessionId = this.eStreamSessionId;
    if (!estreamSessionId) {
      throw new Error('eStream session id is not available');
    }

    return {
      ...this.getResourceHeadersWithToken(),
      estreamSessionId,
    };
  }

  private async prepareEStreamRequest(): Promise<{
    eStreamApi: EstreamApi;
    headers: ResourceHeadersWithToken & { estreamSessionId: string };
  }> {
    // The session can be invalidated concurrently (e.g. a disconnect while the app foregrounds)
    // between resolving the API client and reading the session id, so re-acquire it once if so.
    let eStreamApi = await this.getEstreamApi();
    if (!eStreamApi || !this.eStreamSessionId) {
      eStreamApi = await this.getEstreamApi();
    }

    return { eStreamApi, headers: this.getEStreamRequestHeaders() };
  }

  /**
   * Runs an eStream REST request and retries it once on a 401 (stale session). The retry reuses the
   * current session if it already advanced, otherwise it renews the session first.
   */
  private async executeEStreamRequest<T>(
    request: (eStreamApi: EstreamApi, headers: ResourceHeadersWithToken & { estreamSessionId: string }) => Promise<T>,
  ): Promise<T> {
    const { eStreamApi, headers } = await this.prepareEStreamRequest();
    try {
      return await request(eStreamApi, headers);
    } catch (err) {
      if (!isAxiosError(err) || err.response?.status !== 401) {
        throw err;
      }

      if (this.eStreamSessionId === headers.estreamSessionId) {
        this.invalidateSession();
      }
      const retry = await this.prepareEStreamRequest();
      return request(retry.eStreamApi, retry.headers);
    }
  }

  private eStreamApiInitPromise?: Promise<EstreamApi>;

  /**
   * Returns the eStream API client with a guaranteed session, (re)establishing it via postSessions if
   * missing. Concurrent callers share a single initialization. Resolves with a live client and session
   * or throws, so callers should use try/catch.
   */
  private async getEstreamApi(): Promise<EstreamApi> {
    if (this.eStreamApiInitPromise) {
      return this.eStreamApiInitPromise;
    }

    if (this.eStreamApi && this.eStreamSessionId) {
      if (this.tokenExpiration && isAfter(new Date(), new Date(this.tokenExpiration * 1000))) {
        await this.refreshEStreamSession();
      }
      return this.eStreamApi;
    }

    this.eStreamApiInitPromise = this.initializeEStreamApi();
    try {
      return await this.eStreamApiInitPromise;
    } finally {
      this.eStreamApiInitPromise = undefined;
    }
  }

  private async initializeEStreamApi(): Promise<EstreamApi> {
    if (!this.axiosInstance || !this.getResourceHeadersWithToken) {
      throw new Error(`EStreamClient is not initialized yet`);
    }

    const eStreamApi =
      this.eStreamApi ??
      new EstreamApi(
        new Configuration({ basePath: this.eStreamBaseURL, baseOptions: { timeout: 30000 } }),
        this.eStreamBaseURL,
        this.axiosInstance,
      );
    this.eStreamApi = eStreamApi;

    if (!this.eStreamSessionId) {
      if (this.eStreamSessionIdRefresh) {
        clearTimeout(this.eStreamSessionIdRefresh);
      }

      try {
        const session = (
          await eStreamApi.postSessions(this.getResourceHeadersWithToken(), { baseURL: this.eStreamBaseURL })
        ).data.data;
        if (!session?.session_id || !session?.expiration) {
          throw new Error(`Invalid estream session_id!`);
        }

        // Re-assert in case a concurrent invalidateSession() cleared these while postSessions was in flight.
        this.eStreamApi = eStreamApi;
        this.eStreamSessionId = session.session_id;
        this.tokenExpiration = session.expiration;
        this.handleEStreamSessionIdRefresh(session.expiration);
        this.setToken(session.session_id);
      } catch (err) {
        this.eStreamApi = undefined;
        this.logger.error(`Failed to initialize eStream API`, err);
        throw err;
      }
    }

    return eStreamApi;
  }

  private async refreshEStreamSession(): Promise<void> {
    if (!this.getResourceHeadersWithToken || !this.eStreamApi || !this.eStreamSessionId) {
      return;
    }

    const resp = (
      await this.eStreamApi.patchSessions(
        { ...this.getResourceHeadersWithToken(), estreamSessionId: this.eStreamSessionId },
        { baseURL: this.eStreamBaseURL },
      )
    ).data.data;

    if (this.eStreamSessionIdRefresh) {
      clearTimeout(this.eStreamSessionIdRefresh);
    }
    if (resp?.token) {
      this.setToken(resp.token);
    }
    if (resp?.expiration) {
      this.tokenExpiration = resp.expiration;
      this.handleEStreamSessionIdRefresh(resp.expiration);
    }
  }

  private handleEStreamSessionIdRefresh(expiration: number) {
    const timeout = expiration * 1000 - Date.now() - this.config.SSE.GeneralReconnectTimeout;
    this.eStreamSessionIdRefresh = setTimeout(() => {
      this.refreshEStreamSession().catch(err => this.logger.warn(`eStream session refresh failed`, err));
    }, timeout) as unknown as number;
  }

  refreshMarketRights() {
    this.refreshMarketRightsOnNextSubscribe = true;
    this.sendSubscriptionRequest();
  }

  private invalidateSession() {
    if (this.eStreamSessionIdRefresh) {
      clearTimeout(this.eStreamSessionIdRefresh);
      this.eStreamSessionIdRefresh = undefined;
    }
    this.eStreamApi = undefined;
    this.eStreamSessionId = undefined;
    this.tokenExpiration = undefined;
    this.setToken('');
    this.firstSubscriptionSent = false;
    this.tokenInitializingState = TokenInitializingState.Disconnected;
  }

  private isTokenExpired() {
    return !!this.tokenExpiration && isAfter(new Date(), new Date(this.tokenExpiration * 1000));
  }
}
